import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useDebounce } from "@src/appV2/lib/utils";
import { z } from "zod";

const searchUsersParamsSchema = z.object({
  searchInput: z.string(),
  limit: z.number().optional(),
  skip: z.number().optional(),
});

export const userSchema = z.object({
  userId: z.string(),
  name: z.string(),
});

const searchUsersResponseSchema = z.object({
  list: z.array(userSchema),
});

type UseSearchUsersParams = z.infer<typeof searchUsersParamsSchema> & {
  type: "AGENT" | "FACILITY";
};
type SearchUsersResponse = z.infer<typeof searchUsersResponseSchema>;
export type User = z.infer<typeof userSchema>;

function getSearchUrl(userType: UseSearchUsersParams["type"]): string {
  const searchPath = userType === "AGENT" ? "agentSearch" : "facilitySearch";
  return `${environmentConfig.REACT_APP_BASE_API_URL}/user/${searchPath}`;
}

function mapResponse(data: SearchUsersResponse): SearchUsersResponse {
  return {
    list: data.list
      .filter((user) => user.name)
      .map((user) => ({
        userId: user.userId,
        name: user.name,
      })),
  };
}

/**
 * @deprecated
 * Deprecated, please use:
 * useSearchAgents if type === 'AGENT' or
 * useSearchFacilities if type === 'FACILITY'
 * The new hooks use useInfiniteQuery and are more flexible for pagination
 */
export function useDeprecatedSearchUsers(
  params: UseSearchUsersParams,
  options: Omit<
    UseGetQueryOptions<SearchUsersResponse>,
    "url" | "queryParams" | "requestSchema" | "responseSchema" | "select"
  > = {}
) {
  const { type, limit = 10, skip = 0 } = params;
  const searchInput = useDebounce({ debounceTimeInMs: 200, value: params.searchInput });

  return useGetQuery({
    ...options,
    url: getSearchUrl(type),
    queryParams: { limit, searchInput, skip },
    requestSchema: searchUsersParamsSchema,
    responseSchema: searchUsersResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.SEARCH_USERS_FAILURE,
    },
    select: (data) => mapResponse(data),
    enabled: searchInput.length > 0 && options.enabled,
  });
}
